import { DecksRepository } from '@lib/api/repository/decks/decks.repository';
import {
  Deck,
  DeckBudget,
  DeckEntryInput,
  DeckImportFormat,
  DeckImportMatch,
  DeckInput,
  DeckPublicationInput,
  DeckUpdateInput,
  OptimizedPurchaseEntryInput,
  PublicDeck,
} from '@lib/types';
import {
  DeckCardPage,
  DeckCardSearch,
  DeckValuation,
} from '@lib/types/deck-editor';
import {
  DeckSaleInput,
  DeckSaleOrder,
  DeckSalePage,
  DeckSalePreparation,
  DeckSaleQuote,
  ManageDeckSaleInput,
  PublishDeckSaleInput,
  PublishedDeckSale,
} from '@lib/types/deck-sale';

import { Requester } from '../clients';

interface DeckDTO {
  id: string;
  name: string;
  description?: string;
  format?: Deck['format'];
  kind: Deck['kind'];
  purpose: Deck['purpose'];
  coverCardId?: string;
  coverImageUrl?: string;
  status: Deck['status'];
  visibility: Deck['visibility'];
  game: Deck['game'];
  entries: Array<{
    id: string;
    cardId: string;
    cardName: string;
    imageUrl?: string;
    printingId?: string;
    artworkId?: string;
    languageId?: string;
    condition?: DeckEntryInput['condition'];
    quantity: number;
    ownedQuantity: number;
    section: DeckEntryInput['section'];
  }>;
  unresolvedImports: Array<{
    id: string;
    sourceKey: string;
    rawLine: string;
    name: string;
    quantity: number;
    section: DeckEntryInput['section'];
  }>;
  offer?: {
    id: string;
    storeId: string;
    priceCents: number;
    quantity: number;
    status: 'active' | 'paused';
  };
  updatedAt: string;
}

interface PublicDeckDTO {
  id: string;
  name: string;
  description?: string;
  format?: Deck['format'];
  kind: Deck['kind'];
  purpose: Deck['purpose'];
  coverCardId?: string;
  coverImageUrl?: string;
  status: Deck['status'];
  game: Deck['game'];
  entries: Array<{
    cardId: string;
    cardName: string;
    imageUrl?: string;
    printingId?: string;
    quantity: number;
    section: DeckEntryInput['section'];
  }>;
  offer?: { id: string; priceCents: number; quantity: number };
  updatedAt: string;
}

interface DeckBudgetDTO {
  calculatedAt: string;
  validUntil: string;
  considerCart: boolean;
  requiredUnits: number;
  coveredByOwnedUnits: number;
  coveredByCartUnits: number;
  remainingUnits: number;
  entries: Array<{
    entryId: string;
    cardId: string;
    cardName: string;
    imageUrl?: string;
    requestedQuantity: number;
    ownedQuantity: number;
    cartQuantity: number;
    remainingQuantity: number;
    status: DeckBudget['entries'][number]['status'];
  }>;
  stores: Array<{
    storeId: string;
    storeName: string;
    coveredUnits: number;
    coveredDistinctCards: number;
    coveragePercentage: number;
    totalCents: number;
    items: Array<{
      entryId: string;
      cardId: string;
      cardName: string;
      imageUrl?: string;
      inventoryItemId: string;
      printingId: string;
      printingCode: string;
      collectionName: string;
      rarityName: string;
      condition: DeckBudget['stores'][number]['items'][number]['condition'];
      languageName: string;
      edition: DeckBudget['stores'][number]['items'][number]['edition'];
      requiredQuantity: number;
      availableQuantity: number;
      selectedQuantity: number;
      unitPriceCents: number;
      subtotalCents: number;
      coverage: 'full' | 'partial';
    }>;
  }>;
}

interface DeckMatchDTO {
  sourceKey: string;
  rawLine: string;
  name: string;
  quantity: number;
  section: DeckEntryInput['section'];
  confidence: DeckImportMatch['confidence'];
  candidates: Array<{ id: string; name: string; imageUrl?: string }>;
}

const toDeck = (dto: DeckDTO): Deck => ({
  id: dto.id,
  name: dto.name,
  description: dto.description,
  format: dto.format,
  kind: dto.kind,
  purpose: dto.purpose,
  coverCardId: dto.coverCardId,
  coverImageUrl: dto.coverImageUrl,
  status: dto.status,
  visibility: dto.visibility,
  game: dto.game,
  entries: dto.entries.map((entry) => ({
    id: entry.id,
    cardId: entry.cardId,
    cardName: entry.cardName,
    imageUrl: entry.imageUrl,
    printingId: entry.printingId,
    artworkId: entry.artworkId,
    languageId: entry.languageId,
    condition: entry.condition,
    quantity: entry.quantity,
    ownedQuantity: entry.ownedQuantity,
    section: entry.section,
  })),
  unresolvedImports: dto.unresolvedImports.map((row) => ({
    id: row.id,
    sourceKey: row.sourceKey,
    rawLine: row.rawLine,
    name: row.name,
    quantity: row.quantity,
    section: row.section,
  })),
  offer: dto.offer
    ? {
        id: dto.offer.id,
        storeId: dto.offer.storeId,
        priceCents: dto.offer.priceCents,
        quantity: dto.offer.quantity,
        status: dto.offer.status,
      }
    : undefined,
  updatedAt: dto.updatedAt,
});

const toBudget = (dto: DeckBudgetDTO): DeckBudget => ({
  calculatedAt: dto.calculatedAt,
  validUntil: dto.validUntil,
  considerCart: dto.considerCart,
  requiredUnits: dto.requiredUnits,
  coveredByOwnedUnits: dto.coveredByOwnedUnits,
  coveredByCartUnits: dto.coveredByCartUnits,
  remainingUnits: dto.remainingUnits,
  entries: dto.entries.map((entry) => ({ ...entry })),
  stores: dto.stores.map((store) => ({
    ...store,
    items: store.items.map((item) => ({ ...item })),
  })),
});

export default class DecksAPI implements DecksRepository {
  async prepareSale(
    id: string,
    signal?: AbortSignal,
  ): Promise<DeckSalePreparation> {
    const { data } = await Requester.get<DeckSalePreparation>(
      `/marketplace/decks/${encodeURIComponent(id)}/sale-preparation`,
      { signal },
    );

    return data;
  }

  async quoteSale(
    id: string,
    input: DeckSaleInput,
    signal?: AbortSignal,
  ): Promise<DeckSaleQuote> {
    const { data } = await Requester.post<DeckSaleQuote>(
      `/marketplace/decks/${encodeURIComponent(id)}/sale-quote`,
      { body: input, signal },
    );

    return data;
  }

  async publishSale(
    id: string,
    input: PublishDeckSaleInput,
  ): Promise<PublishedDeckSale> {
    const { data } = await Requester.post<PublishedDeckSale>(
      `/marketplace/decks/${encodeURIComponent(id)}/sales`,
      { body: input },
    );

    return data;
  }

  async manageSale(
    deckId: string,
    saleId: string,
    input: ManageDeckSaleInput,
  ): Promise<PublishedDeckSale> {
    const { data } = await Requester.patch<PublishedDeckSale>(
      `/marketplace/decks/${encodeURIComponent(deckId)}/sales/${encodeURIComponent(saleId)}`,
      { body: input },
    );

    return data;
  }

  async publicSales(
    page: number,
    search?: string,
    signal?: AbortSignal,
  ): Promise<DeckSalePage> {
    const { data } = await Requester.get<DeckSalePage>(
      '/marketplace/decks/sales/public',
      { params: { page, search }, signal },
    );

    return data;
  }

  async publicSale(
    id: string,
    signal?: AbortSignal,
  ): Promise<PublishedDeckSale> {
    const { data } = await Requester.get<PublishedDeckSale>(
      `/marketplace/decks/sales/public/${encodeURIComponent(id)}`,
      { signal },
    );

    return data;
  }

  async ownedSale(
    saleId: string,
    signal?: AbortSignal,
  ): Promise<PublishedDeckSale> {
    const { data } = await Requester.get<PublishedDeckSale>(
      `/marketplace/decks/sales/owned/${encodeURIComponent(saleId)}`,
      { signal },
    );

    return data;
  }

  async saleOrders(
    deckId: string,
    saleId: string,
    signal?: AbortSignal,
  ): Promise<DeckSaleOrder[]> {
    const { data } = await Requester.get<DeckSaleOrder[]>(
      `/marketplace/decks/${encodeURIComponent(deckId)}/sales/${encodeURIComponent(saleId)}/orders`,
      { signal },
    );

    return data;
  }

  async searchCards(
    id: string,
    filters: DeckCardSearch,
    signal?: AbortSignal,
  ): Promise<DeckCardPage> {
    const { data } = await Requester.get<DeckCardPage>(
      `/marketplace/decks/${encodeURIComponent(id)}/cards`,
      {
        signal,
        params: {
          ...filters,
          cardIds: filters.cardIds?.join(','),
          excludeIds: filters.excludeIds?.join(','),
        },
      },
    );

    return data;
  }

  async value(
    id: string,
    entries: DeckEntryInput[],
    signal?: AbortSignal,
  ): Promise<DeckValuation> {
    const { data } = await Requester.post<DeckValuation>(
      `/marketplace/decks/${encodeURIComponent(id)}/valuation`,
      {
        signal,
        body: {
          entries: entries.map((entry) => ({
            card_id: entry.cardId,
            printing_id: entry.printingId,
            artwork_id: entry.artworkId,
            language_id: entry.languageId,
            condition: entry.condition,
            quantity: entry.quantity,
            owned_quantity: entry.ownedQuantity,
            section: entry.section,
          })),
        },
      },
    );

    return data;
  }

  async remove(id: string): Promise<void> {
    await Requester.delete(`/marketplace/decks/${encodeURIComponent(id)}`);
  }

  async listMine(signal?: AbortSignal) {
    const response = await Requester.get<DeckDTO[]>('/marketplace/decks/mine', {
      signal,
    });

    return response.data.map((deck) => toDeck(deck));
  }

  async find(id: string, signal?: AbortSignal) {
    const response = await Requester.get<DeckDTO>(`/marketplace/decks/${id}`, {
      signal,
    });

    return toDeck(response.data);
  }

  async findPublic(id: string, signal?: AbortSignal) {
    const response = await Requester.get<PublicDeckDTO>(
      `/marketplace/decks/public/${id}`,
      { signal },
    );

    const dto = response.data;

    const deck: PublicDeck = {
      id: dto.id,
      name: dto.name,
      description: dto.description,
      format: dto.format,
      kind: dto.kind,
      purpose: dto.purpose,
      coverCardId: dto.coverCardId,
      coverImageUrl: dto.coverImageUrl,
      status: dto.status,
      game: dto.game,
      entries: dto.entries.map((entry) => ({
        cardId: entry.cardId,
        cardName: entry.cardName,
        imageUrl: entry.imageUrl,
        printingId: entry.printingId,
        quantity: entry.quantity,
        section: entry.section,
      })),
      offer: dto.offer,
      updatedAt: dto.updatedAt,
    };

    return deck;
  }

  async create(input: DeckInput) {
    const response = await Requester.post<DeckDTO>('/marketplace/decks', {
      body: {
        ...input,
        game_id: input.gameId,
        cover_card_id: input.coverCardId,
        entries: input.entries?.map((entry) => ({
          card_id: entry.cardId,
          printing_id: entry.printingId,
          artwork_id: entry.artworkId,
          language_id: entry.languageId,
          condition: entry.condition,
          quantity: entry.quantity,
          owned_quantity: entry.ownedQuantity,
          section: entry.section,
        })),
      },
    });

    return toDeck(response.data);
  }

  async update(id: string, input: DeckUpdateInput) {
    const response = await Requester.patch<DeckDTO>(
      `/marketplace/decks/${id}`,
      {
        body: { ...input, cover_card_id: input.coverCardId },
      },
    );

    return toDeck(response.data);
  }

  async replaceEntries(id: string, entries: DeckEntryInput[]) {
    const response = await Requester.put<DeckDTO>(
      `/marketplace/decks/${id}/entries`,
      {
        body: {
          entries: entries.map((entry) => ({
            card_id: entry.cardId,
            printing_id: entry.printingId,
            artwork_id: entry.artworkId,
            language_id: entry.languageId,
            condition: entry.condition,
            quantity: entry.quantity,
            owned_quantity: entry.ownedQuantity,
            section: entry.section,
          })),
        },
      },
    );

    return toDeck(response.data);
  }

  async previewImport(
    gameCode: string,
    content: string,
    format: DeckImportFormat,
  ) {
    const response = await Requester.post<DeckMatchDTO[]>(
      `/marketplace/decks/import/preview/${encodeURIComponent(gameCode)}`,
      { body: { content, format } },
    );

    return response.data.map((row) => ({
      sourceKey: row.sourceKey,
      rawLine: row.rawLine,
      name: row.name,
      quantity: row.quantity,
      section: row.section,
      confidence: row.confidence,
      candidates: row.candidates.map((candidate) => ({
        id: candidate.id,
        name: candidate.name,
        imageUrl: candidate.imageUrl,
      })),
    }));
  }

  async importRows(
    id: string,
    content: string,
    format: DeckImportFormat,
    resolutions: Array<{
      sourceKey: string;
      cardId: string;
      printingId?: string;
    }>,
  ) {
    const response = await Requester.post<DeckDTO>(
      `/marketplace/decks/${id}/import`,
      {
        body: {
          content,
          format,
          resolutions: resolutions.map((resolution) => ({
            source_key: resolution.sourceKey,
            card_id: resolution.cardId,
            printing_id: resolution.printingId,
          })),
        },
      },
    );

    return toDeck(response.data);
  }

  async resolveImportRow(id: string, rowId: string, cardId: string) {
    const response = await Requester.patch<DeckDTO>(
      `/marketplace/decks/${id}/import/${rowId}`,
      {
        body: { card_id: cardId },
      },
    );

    return toDeck(response.data);
  }

  async budget(id: string, considerCart: boolean) {
    const response = await Requester.post<DeckBudgetDTO>(
      `/marketplace/decks/${id}/budget`,
      {
        body: { considerCart },
      },
    );

    return toBudget(response.data);
  }

  async budgetEntries(
    entries: OptimizedPurchaseEntryInput[],
    considerCart: boolean,
  ) {
    const response = await Requester.post<DeckBudgetDTO>(
      '/marketplace/decks/optimized-purchase/budget',
      {
        body: {
          consider_cart: considerCart,
          entries: entries.map((entry) => ({
            card_id: entry.cardId,
            quantity: entry.quantity,
          })),
        },
      },
    );

    return toBudget(response.data);
  }

  async publish(id: string, input: DeckPublicationInput) {
    const response = await Requester.post<DeckDTO>(
      `/marketplace/decks/${id}/publish`,
      {
        body: {
          price_cents: input.priceCents,
          quantity: input.quantity,
          idempotency_key: input.idempotencyKey,
          components: input.components.map((component) => ({
            entry_id: component.entryId,
            printing_id: component.printingId,
            language_id: component.languageId,
            condition: component.condition,
            edition: component.edition,
          })),
        },
      },
    );

    return toDeck(response.data);
  }

  async updateOffer(id: string, status: 'active' | 'paused') {
    const response = await Requester.patch<DeckDTO>(
      `/marketplace/decks/${id}/offer`,
      {
        body: { status },
      },
    );

    return toDeck(response.data);
  }
}
